import React, { useCallback } from 'react';
import { createSelector } from 'reselect';
import { DropdownItem } from '@atlaskit/dropdown-menu';
import SortAscendingIcon from '@atlaskit/icon/core/sort-ascending';
import { Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import DisabledButtonWithTooltip from '@atlassian/jira-disabled-button-with-tooltip/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import {
	AnalyticsEventToProps,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import type { ChildIssuesLimitStatus } from '@atlassian/jira-issue-view-common-constants/src/child-issues.tsx';
import { connect } from '@atlassian/jira-issue-view-react-redux/src/index.tsx';
import { toIssueId, type IssueId } from '@atlassian/jira-shared-types/src/general.tsx';
import {
	hasExceededIssuesLimitAfterLoad,
	hasExceededIssuesLimitInitialLoad,
} from '../../../common/utils/has-exceeded-issues-limit/index.tsx';
import { useChildIssuesSortBy, useSortableIssueIds } from '../../../controllers/main.tsx';
import { getAllIssues } from '../../../state/entities/selectors.tsx';
import type { State } from '../../../state/types.tsx';
import messages from './messages.tsx';

type ReduxStateProps = {
	allIssueIds: IssueId[];
};
type OwnProps = {
	childIssuesLimitStatus: ChildIssuesLimitStatus;
};
export const DropdownItemWithAnalytics = AnalyticsEventToProps('dropdownItem', {
	onClick: 'clicked',
})(DropdownItem);
export type Props = OwnProps & ReduxStateProps;
export const ConnectedComponent = ({ allIssueIds, childIssuesLimitStatus }: Props) => {
	const [sortByValue, { setSortBy }] = useChildIssuesSortBy();
	const { formatMessage } = useIntl();
	const [, { setSortableIssueIds }] = useSortableIssueIds();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const onSelect = useCallback(
		// @ts-expect-error - TS7006 - Parameter 'value' implicitly has an 'any' type.
		(value) => {
			fireUIAnalytics(
				createAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'dropdown',
				}),
				'childIssuesSortBy',
				{
					selection: value,
				},
			);
			setSortableIssueIds(allIssueIds);
			setSortBy(value);
		},
		[allIssueIds, createAnalyticsEvent, setSortBy, setSortableIssueIds],
	);
	const hasExceededLimitAfterLoad = hasExceededIssuesLimitAfterLoad(childIssuesLimitStatus);
	const button = useCallback(
		() => (
			<DropdownItem
				elemBefore={<SortAscendingIcon color={token('color.icon')} label="" />}
				testId="issue-view-common-views.child-issues-panel.heading.sort-by-rank-button.trigger-button"
				isDisabled={hasExceededLimitAfterLoad}
				// @ts-expect-error - TS7006 - Parameter 'event' implicitly has an 'any' type. | TS7006 - Parameter 'analyticsEvent' implicitly has an 'any' type.
				onClick={(event, analyticsEvent) => onSelect(sortByValue, analyticsEvent)}
			>
				<Text>{formatMessage(messages.sortByRanking)}</Text>
			</DropdownItem>
		),
		[formatMessage, hasExceededLimitAfterLoad, onSelect, sortByValue],
	);
	if (hasExceededIssuesLimitInitialLoad(childIssuesLimitStatus)) {
		return null;
	}
	if (hasExceededLimitAfterLoad) {
		return (
			<Tooltip
				content={formatMessage(
					fg('jira-issue-terminology-refresh-m3')
						? messages.exceededChildIssuesLimitIssueTermRefresh
						: messages.exceededChildIssuesLimit,
				)}
				position="top"
			>
				<DisabledButtonWithTooltip isDisabled={hasExceededLimitAfterLoad}>
					{button()}
				</DisabledButtonWithTooltip>
			</Tooltip>
		);
	}
	return button();
};
const toIssueIds = createSelector(getAllIssues, (issues) =>
	issues.map((issue) => toIssueId(issue.id)),
);
const ReduxConnectedComponent = connect(
	(state: State): ReduxStateProps => ({
		allIssueIds: toIssueIds(state),
	}),
	{},
)(ConnectedComponent);
export default ReduxConnectedComponent;
